// ** React Imports
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

// ** Next Imports

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Tooltip from '@mui/material/Tooltip'
import { styled, createTheme } from '@mui/material/styles'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import MuiTabList from '@mui/lab/TabList'
import LinearProgress from '@mui/material/LinearProgress'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Styled Components
import CustomHeader from 'src/@core/components/Header'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import {
  Alert,
  AlertTitle,
  AppBar,
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  useMediaQuery
} from '@mui/material'
import { amber, blue, deepPurple, green, red } from '@mui/material/colors'
import { TabContext, TabPanel } from '@mui/lab'
import {
  AccountTree,
  AddSharp,
  AddToPhotos,
  ClearAll,
  LibraryAdd,
  Looks3,
  Looks4,
  LooksOne,
  LooksTwo,
  NewReleases,
  RemoveSharp
} from '@mui/icons-material'

import {
  totemConf,
  totemTemplates,
  totemInfo,
  totemStones,
  totemSkill,
  totemStats,
  totemList,
  totemSlots,
  totemRewards
} from 'src/data/totem'

// ** Styled component for the link in the dataTable
const theme = createTheme()

const GreenButton = styled(Button)(({ theme }) => ({
  // Verde oscuro y texto blanco: #E8F1F5 sobre #1B8A6B daba 3,74:1, por debajo de AA.
  backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
    filter: 'brightness(0.85)'
  }
}))

const BlueButton = styled(Button)(({ theme }) => ({
  /*
    blue[800] y no blue[600]. getContrastText elige blanco con un umbral de 3:1,
    asi que sobre blue[600] (#1e88e5) daba 3,67:1 — por debajo del 4,5:1 de AA.
    blue[800] (#1565c0) con blanco da 5,75 y sigue leyendose como el azul de la
    calidad. El hover baja a blue[900].
  */
  color: theme.palette.getContrastText(blue[800]),
  backgroundColor: blue[800],
  '&:hover': {
    backgroundColor: blue[900]
  }
}))

const PurpleButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(deepPurple[400]),
  backgroundColor: deepPurple[400],
  '&:hover': {
    backgroundColor: deepPurple[800]
  }
}))

const GoldButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(amber[400]),
  backgroundColor: amber[400],
  '&:hover': {
    backgroundColor: amber[700]
  }
}))

/*
  Tablas de referencia renderizadas en el servidor.

  Por que existe esto aca: /totem/ servia 469 palabras. Es el dataset mas grande
  del repo —328 KB entre totemStats y totemStones— y era la pagina con menos texto
  indexable del sitio: ni los nombres de siete de los ocho totems salian en el HTML,
  porque todo vive detras de seleccionar un slot.

  Es el mismo defecto que se corrigio en septiembre con TablaReferencia, cuando
  /building paso de 676 a 5.126 palabras. Aquella tanda cubrio /building, /database,
  /equipment, /realms y /artifacts; /totem se quedo fuera.

  Se publican dos tablas y no las 2.760 filas de totemStats: dentro de una calidad
  las ocho totems comparten exactamente el mismo coste —comprobado, no supuesto— asi
  que publicar las ocho seria repetir la misma secuencia ocho veces. La primera tabla
  resume los ocho; la segunda da la secuencia de coste por calidad, que es el dato
  que se consulta.
*/
const COLUMNAS_TOTEMS = [
  { clave: 'desc', encabezado: 'Totem' },
  { clave: 'quality', encabezado: 'Quality' },
  { clave: 'nodes', encabezado: 'Nodes', numerica: true },
  { clave: 'maxLevel', encabezado: 'Max level', numerica: true },
  { clave: 'total', encabezado: 'Runestones to max', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'bonusA', encabezado: 'First milestone' },
  { clave: 'bonusB', encabezado: 'Second milestone' }
]

const FILAS_TOTEMS = totemList.map(x => ({
  key: x.key,
  desc: x.desc,
  quality: x.quality,
  nodes: x.nodes,
  maxLevel: x.maxLevel,
  total: (totemStats[x.key] ?? []).reduce((a, f) => a + (f.stones ?? 0), 0),
  bonusA: `Lv ${x.bonusA.level} — ${x.bonusA.statDesc} +${x.bonusA.stat}%`,
  bonusB: `Lv ${x.bonusB.level} — ${x.bonusB.statDesc} +${x.bonusB.stat}%`
}))

const COLUMNAS_PIEDRAS = [
  { clave: 'quality', encabezado: 'Quality' },
  { clave: 'level', encabezado: 'Level', numerica: true },
  { clave: 'stones', encabezado: 'Runestones for this level', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'acumulado', encabezado: 'Cumulative, one node', numerica: true, formato: v => v.toLocaleString('en-US') }
]

/*
  Una fila por nivel y calidad. Sin la columna de nodo, y no por ahorrar espacio:
  dentro de un nivel los tres nodos cuestan exactamente lo mismo —comprobado sobre
  las tres calidades—, asi que una fila por nodo seria la misma cifra repetida dos o
  tres veces. Con nodo eran 1.020 filas y 885 KB de HTML; sin el son 360.

  Lo que si cambia por nodo es la stat que da cada uno, y eso no esta en esta tabla:
  aca se responde "que cuesta este nivel", que es la consulta de planificacion.

  El coste se toma del primer totem de cada calidad porque los demas lo repiten
  exactamente. El acumulado es el de UN nodo; el totem entero cuesta ese acumulado
  multiplicado por su numero de nodos.
*/
const FILAS_PIEDRAS = (() => {
  const representante = { blue: 'fertility', purple: 'fire', gold: 'earth' }
  const filas = []
  for (const [calidad, key] of Object.entries(representante)) {
    const porNivel = new Map()
    for (const p of totemStats[key] ?? []) if (!porNivel.has(p.level)) porNivel.set(p.level, p.stones ?? 0)

    let acumulado = 0
    for (const nivel of [...porNivel.keys()].sort((a, b) => a - b)) {
      const stones = porNivel.get(nivel)
      acumulado += stones
      filas.push({ id: `${calidad}-${nivel}`, quality: calidad, level: nivel, stones, acumulado })
    }
  }

  return filas
})()

const TabList = styled(MuiTabList)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'none'
  },
  '& .Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    // contrastText: en modo oscuro el primary es claro y el blanco daba 2,30:1
    color: `${theme.palette.primary.contrastText} !important`
  },
  '& .MuiTab-root': {
    minWidth: 65,
    minHeight: 38,
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('sm')]: {
      minWidth: 130
    }
  }
}))

// ** Custom Functions
function round(value, precision) {
  var multiplier = Math.pow(10, precision || 0)

  return Math.round(value * multiplier) / multiplier
}

function generateStats(data) {
  if (data?.length > 0) {
    let generalStats = data.map(d => {
      return { statDesc: d.statDesc, stat: d.stat }
    })

    const generalStatsGrouped = [
      ...generalStats
        .reduce((r, o) => {
          const key = o.statDesc
          const item = r.get(key) || Object.assign({}, o, { stat: 0 })
          item.stat += o.stat * 1

          return r.set(key, item)
        }, new Map())
        .values()
    ]

    generalStatsGrouped.sort((a, b) => (a.statDesc > b.statDesc ? 1 : -1))

    return generalStatsGrouped
  } else {
    return []
  }
}

function getStats(mode, totem, level, params) {
  if (!totem) return []

  let list1

  // Auto
  if (mode === 1) {
    if (totem.nodes === 2) {
      const level1 = (level / 2) % 2 === 0 ? level / 2 : Math.trunc(level / 2) + 1
      const level2 = (level / 2) % 2 === 0 ? level / 2 : Math.trunc(level / 2)
      const arrayExtra1 = []
      const arrayExtra2 = []

      if (level === 1) {
        list1 = [...totemStats[totem.key]]?.filter(t => t.node === 1 && t.level === level)

        return list1
      }

      if (level1 >= totem.bonusA.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level1 >= totem.bonusB.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })
      if (level2 >= totem.bonusA.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level2 >= totem.bonusB.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })

      list1 = [...totemStats[totem.key]]?.filter(t => t.node === 1 && t.level <= level1)
      const list2 = [...totemStats[totem.key]]?.filter(t => t.node === 1 && t.level <= level2)

      const newArray = [...list1, ...list2, ...arrayExtra1, ...arrayExtra2]

      return newArray
    } else if (totem.nodes === 3) {
      const level1 = (level / 3) % 2 === 0 ? level / 3 : Math.trunc(level / 3)
      const level2 = (level / 3) % 2 === 0 ? level / 3 : Math.trunc(level / 3)
      const level3 = (level / 3) % 2 === 0 ? level / 3 : level - (level1 + level2)
      const arrayExtra1 = []
      const arrayExtra2 = []
      const arrayExtra3 = []

      if (level === 1) {
        list1 = [...totemStats[totem.key]]?.filter(t => t.node === 1 && t.level === level)

        return list1
      }

      if (level1 >= totem.bonusA.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level1 >= totem.bonusB.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })
      if (level2 >= totem.bonusA.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level2 >= totem.bonusB.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })
      if (level3 >= totem.bonusA.level)
        arrayExtra3.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level3 >= totem.bonusB.level)
        arrayExtra3.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })

      list1 = [...totemStats[totem.key]]?.filter(t => t.node === 1 && t.level <= level1)
      const list2 = [...totemStats[totem.key]]?.filter(t => t.node === 2 && t.level <= level2)
      const list3 = [...totemStats[totem.key]]?.filter(t => t.node === 3 && t.level <= level3)
      const newArray = [...list1, ...list2, ...list3, ...arrayExtra1, ...arrayExtra2, ...arrayExtra3]

      return newArray
    }
  }

  // Custom
  if (mode === 2) {
    if (totem.nodes === 2) {
      const level1 = params[1]
      const level2 = params[2]
      const arrayExtra1 = []
      const arrayExtra2 = []

      if (level1 >= totem.bonusA.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level1 >= totem.bonusB.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })
      if (level2 >= totem.bonusA.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level2 >= totem.bonusB.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })

      const list1 = [...totemStats[totem.key]]?.filter(t => t.node === 1 && t.level <= level1)
      const list2 = [...totemStats[totem.key]]?.filter(t => t.node === 2 && t.level <= level2)

      const newArray = [...list1, ...list2, ...arrayExtra1, ...arrayExtra2]

      return newArray
    } else if (totem.nodes === 3) {
      const level1 = params[1]
      const level2 = params[2]
      const level3 = params[3]
      const arrayExtra1 = []
      const arrayExtra2 = []
      const arrayExtra3 = []

      if (level1 >= totem.bonusA.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level1 >= totem.bonusB.level)
        arrayExtra1.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })
      if (level2 >= totem.bonusA.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level2 >= totem.bonusB.level)
        arrayExtra2.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })
      if (level3 >= totem.bonusA.level)
        arrayExtra3.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusA.statDesc, stat: totem.bonusA.stat })
      if (level3 >= totem.bonusB.level)
        arrayExtra3.push({ node: 0, level: 0, stones: 0, statDesc: totem.bonusB.statDesc, stat: totem.bonusB.stat })

      const list1 = [...totemStats[totem.key]]?.filter(t => t.node === 1 && t.level <= level1)
      const list2 = [...totemStats[totem.key]]?.filter(t => t.node === 2 && t.level <= level2)
      const list3 = [...totemStats[totem.key]]?.filter(t => t.node === 3 && t.level <= level3)
      const newArray = [...list1, ...list2, ...list3, ...arrayExtra1, ...arrayExtra2, ...arrayExtra3]

      return newArray
    }
  }

  return []
}

function getStatUpDown(blue, purple, gold) {
  const colorList = { blue, purple, gold }

  return Object.keys(colorList).reduce((a, b) => (colorList[a] > colorList[b] ? a : b))
}

function getDaysNeeded(type, runestones) {
  let outputData = []
  const filterData = [...totemRewards].filter(tr => tr.group === type)

  if (filterData?.length > 0) {
    filterData.forEach((row, idx) => {
      const calculate = row.qty * row.times
      const calculateDays = calculate / (row.event === 'daily' ? 1 : 7)
      outputData.push(calculateDays)
    })
  }

  const daysNeeded = `${Math.round(
    runestones / outputData.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
  )?.toLocaleString()} day/s to get needed ${filterData.find(tr => tr.group === type).type}`

  return { result: daysNeeded }
}

// ** Custom Vars
const totemCalculator = [
  {
    quality: 'blue',
    desc: 'Blue',
    maxLevel: 120,
    maxSkill: 10,
    levelFrom: 0,
    levelTo: 0,
    skillFrom: 0,
    skillTo: 0,
    stones: 0,
    tattoos: 0,
    userStones: 0,
    userTattoos: 0
  },
  {
    quality: 'purple',
    desc: 'Purple',
    maxLevel: 450,
    maxSkill: 30,
    levelFrom: 0,
    levelTo: 0,
    skillFrom: 0,
    skillTo: 0,
    stones: 0,
    tattoos: 0,
    userStones: 0,
    userTattoos: 0
  },
  {
    quality: 'gold',
    desc: 'Gold',
    maxLevel: 450,
    maxSkill: 30,
    levelFrom: 0,
    levelTo: 0,
    skillFrom: 0,
    skillTo: 0,
    stones: 0,
    tattoos: 0,
    userStones: 0,
    userTattoos: 0
  }
]

/*
  `fg` es el color de texto que si contrasta contra cada fondo. Las celdas de la
  tabla de calidades pasaban color='black' a Typography, que MUI ignora por no
  ser una clave de la paleta: el texto terminaba en blanco y daba 3,68:1 sobre el
  azul. Medido por color: azul con negro 5,71, morado con blanco 5,21, dorado con
  negro 13,73. Los fondos no cambian — son los colores del juego.
*/
const totemColors = {
  blue: { bg: '#1e88e5', fg: '#000000' },
  purple: { bg: '#7e57c2', fg: '#FFFFFF' },
  gold: { bg: '#ffca28', fg: '#000000' }
}

 
const Totems = () => {
  // ** State
  const [activeTab, setActiveTab] = useState('runestones')
  const [isLoading, setIsLoading] = useState(false)
  const hideText = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const [snackOpen, setSnackOpen] = useState({ open: false })
  const [slotsInfo, setSlotsInfo] = useState(totemSlots)
  const [selectedTemplate, setSelectedTemplate] = useState('')

  const [calculator, setCalculator] = useState(totemCalculator)

  const generalItemList = generateStats(
    Array.prototype.concat.apply(
      [],
      slotsInfo.map(s => s?.generalStats)
    )
  )

  // ** Hooks

  const handleLoadTemplateChange = e => {
    const newTemplate = e.target.value
    const templateObj = totemTemplates?.find(at => at.key === newTemplate)

    let newSlotsInfo = [...totemSlots]

    if (templateObj) {
      // Blue
      if (templateObj.config.blueLevel > 0) {
        const slot = 1
        const level = templateObj.config.blueLevel
        const totem = 'fertility'
        const slotObj = slotsInfo?.find(s => s.slot === slot)
        const totemObj = totemList.find(t => t.key === totem)

        const totemStats = getStats(slotObj?.mode, totemObj, level, {})
        const totalStones = totemStats.reduce((accumulator, currentValue) => accumulator + currentValue.stones, 0)

        const generalStats = generateStats(totemStats)

        newSlotsInfo = newSlotsInfo.map(obj =>
          obj.slot === slot
            ? {
                ...slotObj,
                totem: totem,
                totemObj: totemObj,
                level: level,
                generalStats: generalStats,
                stones: totalStones
              }
            : obj
        )
      }

      // Purple
      if (templateObj.config.purpleLevel > 0) {
        const slot = 2
        const level = templateObj.config.purpleLevel
        const totem = 'fire'
        const slotObj = slotsInfo?.find(s => s.slot === slot)
        const totemObj = totemList.find(t => t.key === totem)

        const totemStats = getStats(slotObj?.mode, totemObj, level, {})
        const totalStones = totemStats.reduce((accumulator, currentValue) => accumulator + currentValue.stones, 0)

        const generalStats = generateStats(totemStats)

        newSlotsInfo = newSlotsInfo.map(obj =>
          obj.slot === slot
            ? {
                ...slotObj,
                totem: totem,
                totemObj: totemObj,
                level: level,
                generalStats: generalStats,
                stones: totalStones
              }
            : obj
        )
      }

      // Gold
      if (templateObj.config.goldLevel > 0) {
        const slot = 3
        const level = templateObj.config.goldLevel
        const totem = 'earth'
        const slotObj = slotsInfo?.find(s => s.slot === slot)
        const totemObj = totemList.find(t => t.key === totem)

        const totemStats = getStats(slotObj?.mode, totemObj, level, {})
        const totalStones = totemStats.reduce((accumulator, currentValue) => accumulator + currentValue.stones, 0)

        const generalStats = generateStats(totemStats)

        newSlotsInfo = newSlotsInfo.map(obj =>
          obj.slot === slot
            ? {
                ...slotObj,
                totem: totem,
                totemObj: totemObj,
                level: level,
                generalStats: generalStats,
                stones: totalStones
              }
            : obj
        )
      }

      setSlotsInfo(newSlotsInfo)
      setSelectedTemplate(newTemplate)
    }
  }

  const handleClearAll = e => {
    setSlotsInfo(totemSlots)
    setSelectedTemplate(null)
  }

  const handleTotemChange = (slot, totem) => {
    const slotObj = slotsInfo?.find(s => s.slot === slot)

    if (slotObj) {
      const totemObj = totemList.find(t => t.key === totem)

      const newSlotsInfo = slotsInfo.map(obj =>
        obj.slot === slot ? { ...slotObj, totem: totem, totemObj: totemObj } : obj
      )
      setSlotsInfo(newSlotsInfo)
    }
  }

  const handleTotemModeChange = (slot, mode) => {
    const slotObj = slotsInfo?.find(s => s.slot === slot)

    if (slotObj) {
      const newSlotsInfo = slotsInfo.map(obj => (obj.slot === slot ? { ...slotObj, mode: mode } : obj))
      setSlotsInfo(newSlotsInfo)
    }
  }

  const handleTotemLevelChange = (slot, level) => {
    const slotObj = slotsInfo?.find(s => s.slot === slot)

    if (slotObj) {
      const totemStats = getStats(slotObj?.mode, slotObj?.totemObj, level, {})
      const totalStones = totemStats.reduce((accumulator, currentValue) => accumulator + currentValue.stones, 0)

      const generalStats = generateStats(totemStats)

      const newSlotsInfo = slotsInfo.map(obj =>
        obj.slot === slot ? { ...slotObj, level: level, generalStats: generalStats, stones: totalStones } : obj
      )

      setSlotsInfo(newSlotsInfo)
    }
  }

  const handleTotemNodeLevelChange = (slot, node, level) => {
    const slotObj = slotsInfo?.find(s => s.slot === slot)

    if (slotObj) {
      let newNodes = {}

      if (node === 1) newNodes = { ...slotObj.nodes, 1: level }
      if (node === 2) newNodes = { ...slotObj.nodes, 2: level }
      if (node === 3) newNodes = { ...slotObj.nodes, 3: level }

      const totemStats = getStats(slotObj?.mode, slotObj?.totemObj, level, newNodes)
      const totalStones = totemStats.reduce((accumulator, currentValue) => accumulator + currentValue.stones, 0)

      const generalStats = generateStats(totemStats)

      const newSlotsInfo = slotsInfo.map(obj =>
        obj.slot === slot
          ? { ...slotObj, level: node, generalStats: generalStats, stones: totalStones, nodes: newNodes }
          : obj
      )

      setSlotsInfo(newSlotsInfo)
    }
  }

  const handleCalculatorChange = (quality, from, to, type) => {
    const caltulatorObj = calculator.find(t => t.quality === quality)
    const calcFrom = from > 0 ? from : 1
    const calcTo = to > 0 ? to : from
    let newCalculatorState = [...calculator]

    if (type === 'skill') {
      const newLevelsList = [...totemSkill[quality]]

      const newQuantity = newLevelsList
        ?.filter(a => a.level >= (calcFrom === 1 ? calcFrom : calcFrom + 1) && a.level <= calcTo)
        ?.reduce((accumulator, currentValue) => accumulator + currentValue.quantity, 0)

      newCalculatorState = newCalculatorState.map(totem =>
        totem.quality === quality
          ? { ...caltulatorObj, skillFrom: calcFrom, skillTo: calcTo, tattoos: newQuantity }
          : totem
      )
    }

    if (type === 'stones') {
      const newLevelsList = [...totemStones[quality]]

      const newQuantity = newLevelsList
        ?.filter(a => a.level >= (calcFrom === 1 ? calcFrom : calcFrom + 1) && a.level <= calcTo)
        ?.reduce((accumulator, currentValue) => accumulator + currentValue.quantity, 0)

      newCalculatorState = newCalculatorState.map(totem =>
        totem.quality === quality
          ? { ...caltulatorObj, levelFrom: calcFrom, levelTo: calcTo, stones: newQuantity }
          : totem
      )
    }

    if (type === 'skillInput') {
      newCalculatorState = newCalculatorState.map(totem =>
        totem.quality === quality ? { ...caltulatorObj, userTattoos: to } : totem
      )
    }

    if (type === 'stonesInput') {
      newCalculatorState = newCalculatorState.map(totem =>
        totem.quality === quality ? { ...caltulatorObj, userStones: to } : totem
      )
    }

    setCalculator(newCalculatorState)
  }

  const handleChange = (event, value) => {
    setActiveTab(value)
  }

  const snackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackOpen({ open: false })
  }

  const tableIcons = {
    up: <Icon icon='icon-park-solid:up-two' fontSize='18' color='green' />,
    down: <Icon icon='icon-park-solid:down-two' fontSize='18' color='red' />
  }

  const tabContentList = {
    runestones: (
      <>
        <Card>
          <Box sx={{ flexGrow: 1 }}>
            <Snackbar
              open={snackOpen.open ? true : false}
              autoHideDuration={snackOpen.time ?? 7000}
              onClose={snackClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
                <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
                {snackOpen.open}
              </Alert>
            </Snackbar>
            <Grid container spacing={3} sx={{}}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}key={'header-totem'}>
                <Paper sx={{ alignItems: 'center' }}>
                  <Typography
                    variant='h5' component='p'
                    color='primary'
                    align='center'
                    sx={{ marginBottom: '10px', marginTop: '10px' }}
                  >
                    {'RUNESTONES STATS COMPARATOR'}
                  </Typography>
                  <Divider sx={{ marginBottom: '5px' }} />

                  <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                    <Tooltip describeChild title={'Clear all the information.'}>
                      <Button
                        edge='start'
                        color='error'
                        variant='contained'
                        startIcon={<ClearAll />}
                        onClick={handleClearAll}
                      >
                        START OVER
                      </Button>
                    </Tooltip>
                  </Box>

                  <Divider sx={{ marginBottom: '10px' }} />

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <TextField
                      value={selectedTemplate ? selectedTemplate : ''}
                      align='center'
                      label='Load Presets'
                      id='load-template-select'
                      size='small'
                      variant='outlined'
                      type='number'
                      sx={{
                        minWidth: '155px',
                        marginLeft: '5px'
                      }}
                      select
                      onChange={handleLoadTemplateChange}
                    >
                      {totemTemplates?.map(option => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.desc}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Divider sx={{ marginBottom: '10px', marginTop: '10px' }} />

                  <Box sx={{ padding: '1.5rem' }}>
                    <Grid container spacing={3}>
                        {slotsInfo?.map(slot => {
                          const totemListFiltered = [...totemList]?.filter(t => t.quality === slot.quality)

                          const packPrice = totemInfo.stone[slot.quality]?.avgPrice

                          return (
                            <Grid size={{ xs: 6, lg: 4 }}key={slot.quality}>
                              <Paper elevation={12} sx={{ border: `2px solid ${totemColors[slot.quality].bg}` }}>
                                <Box
                                  key={slot.quality}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Box
                                    sx={{
                                      marginTop: '15px'
                                    }}
                                  />
                                  {slot.quality === 'blue' && <BlueButton>{slot.quality}</BlueButton>}
                                  {slot.quality === 'purple' && <PurpleButton>{slot.quality}</PurpleButton>}
                                  {slot.quality === 'gold' && <GoldButton>{slot.quality}</GoldButton>}

                                  <Divider light sx={{ marginTop: '5px', marginBottom: '8px' }} />

                                  <TextField
                                    value={slot.totem}
                                    align='center'
                                    label='Totem'
                                    id='load-totem-select'
                                    size='small'
                                    variant='outlined'
                                    select
                                    onChange={e => {
                                      handleTotemChange(slot.slot, e.target.value)
                                    }}
                                  >
                                    {totemListFiltered.map(option => (
                                      <MenuItem key={option.key} value={option.key}>
                                        {option.desc}
                                      </MenuItem>
                                    ))}
                                  </TextField>

                                  <Divider light sx={{ marginTop: '5px', marginBottom: '8px' }} />

                                  <TextField
                                    value={slot.mode}
                                    align='center'
                                    label='Mode'
                                    id='mode-totem-select'
                                    size='small'
                                    variant='outlined'
                                    disabled={slot.totem.length > 0 ? false : true}
                                    select
                                    onChange={e => {
                                      handleTotemModeChange(slot.slot, e.target.value)
                                    }}
                                  >
                                    {totemConf.levelType.map(option => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.desc}
                                      </MenuItem>
                                    ))}
                                  </TextField>

                                  <Divider light sx={{ marginTop: '5px', marginBottom: '8px' }} />

                                  {/* AUTO MODE */}
                                  {slot.mode === 1 && (
                                    <TextField
                                      id='level'
                                      size='small'
                                      align='center'
                                      sx={{ maxWidth: '105px' }}
                                      disabled={slot.totem.length > 0 ? false : true}
                                      variant='outlined'
                                      type='number'
                                      placeholder='Level'
                                      value={slot.level ? slot.level : ''}
                                      inputProps={{
                                        min: 0,
                                        max: slot.maxLevel
                                      }}
                                      onChange={e => {
                                        const level =
                                          +e.target.value >= slot?.totemObj?.maxLevel * slot?.totemObj?.nodes
                                            ? slot?.totemObj?.maxLevel * slot?.totemObj?.nodes
                                            : +e.target.value

                                        handleTotemLevelChange(slot.slot, level)
                                      }}
                                    />
                                  )}

                                  {/* CUSTOM MODE */}
                                  {slot.mode === 2 && (
                                    <>
                                      <Typography
                                        variant='caption'
                                        color='textPrimary'
                                        align='center'
                                        sx={{ marginTop: '5px' }}
                                      >
                                        Node 1 - (I):
                                      </Typography>
                                      <TextField
                                        slotProps={{ htmlInput: { 'aria-label': 'Node 1 - (I)' } }}
                                        id='level'
                                        size='small'
                                        sx={{ maxWidth: '105px' }}
                                        disabled={slot.totem.length > 0 ? false : true}
                                        variant='outlined'
                                        type='number'
                                        placeholder='Level'
                                        value={slot.nodes[1] ? slot.nodes[1] : ''}
                                        inputProps={{
                                          min: 0,
                                          max: slot.maxLevel
                                        }}
                                        onChange={e => {
                                          const level =
                                            +e.target.value >= slot?.totemObj?.maxLevel
                                              ? slot?.totemObj?.maxLevel
                                              : +e.target.value

                                          handleTotemNodeLevelChange(slot.slot, 1, level)
                                        }}
                                      />

                                      <Typography
                                        variant='caption'
                                        color='textPrimary'
                                        align='center'
                                        sx={{ marginTop: '5px' }}
                                      >
                                        Node 2 - (II):
                                      </Typography>
                                      <TextField
                                        slotProps={{ htmlInput: { 'aria-label': 'Node 2 - (II)' } }}
                                        id='level'
                                        size='small'
                                        sx={{ maxWidth: '105px' }}
                                        disabled={slot.totem.length > 0 ? false : true}
                                        variant='outlined'
                                        type='number'
                                        placeholder='Level'
                                        value={slot.nodes[2] ? slot.nodes[2] : ''}
                                        inputProps={{
                                          min: 0,
                                          max: slot.maxLevel
                                        }}
                                        onChange={e => {
                                          const level =
                                            +e.target.value >= slot?.totemObj?.maxLevel
                                              ? slot?.totemObj?.maxLevel
                                              : +e.target.value

                                          handleTotemNodeLevelChange(slot.slot, 2, level)
                                        }}
                                      />
                                      {slot?.totemObj?.nodes === 3 && (
                                        <>
                                          <Typography
                                            variant='caption'
                                            color='textPrimary'
                                            align='center'
                                            sx={{ marginTop: '5px' }}
                                          >
                                            Node 3 - (III):
                                          </Typography>
                                          <TextField
                                            slotProps={{ htmlInput: { 'aria-label': 'Node 3 - (III)' } }}
                                            id='level'
                                            size='small'
                                            sx={{ maxWidth: '105px' }}
                                            disabled={slot.totem.length > 0 ? false : true}
                                            variant='outlined'
                                            type='number'
                                            placeholder='Level'
                                            value={slot.nodes[3] ? slot.nodes[3] : ''}
                                            inputProps={{
                                              min: 0,
                                              max: slot.maxLevel
                                            }}
                                            onChange={e => {
                                              const level =
                                                +e.target.value >= slot?.totemObj?.maxLevel
                                                  ? slot?.totemObj?.maxLevel
                                                  : +e.target.value

                                              handleTotemNodeLevelChange(slot.slot, 3, level)
                                            }}
                                          />
                                        </>
                                      )}
                                    </>
                                  )}

                                  <Divider light sx={{ marginTop: '5px', marginBottom: '5px' }} />

                                  <Typography
                                    variant='caption'
                                    color='textPrimary'
                                    align='center'
                                    sx={{ marginTop: '10px' }}
                                  >
                                    Total Levels:
                                  </Typography>

                                  <Typography variant='h5' component='p' color='primary' align='center' sx={{ marginTop: '10px' }}>
                                    {slot.mode === 1 ? slot.level : slot.nodes[1] + slot.nodes[2] + slot.nodes[3]}
                                  </Typography>

                                  <Divider light sx={{ marginTop: '5px', marginBottom: '5px' }} />

                                  <Typography
                                    variant='caption'
                                    color='textPrimary'
                                    align='center'
                                    sx={{ marginTop: '10px' }}
                                  >
                                    Total Stones:
                                  </Typography>

                                  <Typography variant='h5' component='p' color='primary' align='center' sx={{ marginTop: '10px' }}>
                                    {slot.stones.toLocaleString()}
                                  </Typography>

                                  {slot.stones > 0 && (
                                    <>
                                      <Typography
                                        variant='caption'
                                        color='textPrimary'
                                        align='center'
                                        sx={{ marginTop: '10px' }}
                                      >
                                        Average Price ($):
                                      </Typography>

                                      <Typography
                                        variant='h5' component='p'
                                        color='primary'
                                        align='center'
                                        sx={{ marginTop: '10px' }}
                                      >
                                        {`$${round(
                                          (slot.stones / packPrice?.qty) * packPrice?.p,
                                          0
                                        )?.toLocaleString()} +-`}
                                      </Typography>
                                    </>
                                  )}

                                  <Box sx={{ marginBottom: '15px' }} />
                                </Box>
                              </Paper>
                            </Grid>
                          )
                        })}
                      </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ marginBottom: '10px' }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Paper elevation={12} sx={{ padding: '0.5rem' }}>
                  <Typography variant='h6' component='h2' color='primary' align='center'>
                    {'STATS COMPARISON CHART'}
                  </Typography>
                  <Divider sx={{ marginBottom: '10px' }} />

                  {generalItemList?.length > 0 && (
                    <>
                      <Paper elevation={12}>
                        <Box>
                          <TableContainer>
                            <Table aria-labelledby='tableTitle' size={'small'} aria-label='enhanced table'>
                              <TableBody>
                                <TableRow>
                                  <TableCell align='center' color='black' sx={{ backgroundColor: `#a0a3a5c4` }}>
                                    {
                                      <Typography variant='body2' color='black'>
                                        Stat
                                      </Typography>
                                    }
                                  </TableCell>
                                  <TableCell align='center' sx={{ backgroundColor: `${totemColors['blue'].bg}` }}>
                                    {
                                      <Typography variant='body2' sx={{ color: totemColors['blue'].fg }}>
                                        BLUE
                                      </Typography>
                                    }
                                  </TableCell>
                                  <TableCell align='center' sx={{ backgroundColor: `${totemColors['purple'].bg}` }}>
                                    {
                                      <Typography variant='body2' sx={{ color: totemColors['purple'].fg }}>
                                        PURPLE
                                      </Typography>
                                    }
                                  </TableCell>
                                  <TableCell align='center' sx={{ backgroundColor: `${totemColors['gold'].bg}` }}>
                                    {
                                      <Typography variant='body2' sx={{ color: totemColors['gold'].fg }}>
                                        GOLD
                                      </Typography>
                                    }
                                  </TableCell>
                                </TableRow>

                                {generalItemList?.map(row => {
                                  const blueRow = slotsInfo
                                    ?.find(s => s.slot === 1)
                                    ?.generalStats?.find(stat => stat.statDesc === row.statDesc) ?? { stat: 0 }

                                  const purpleRow = slotsInfo
                                    ?.find(s => s.slot === 2)
                                    ?.generalStats?.find(stat => stat.statDesc === row.statDesc) ?? { stat: 0 }

                                  const goldRow = slotsInfo
                                    ?.find(s => s.slot === 3)
                                    ?.generalStats?.find(stat => stat.statDesc === row.statDesc) ?? { stat: 0 }

                                  return (
                                    <>
                                      <TableRow>
                                        <TableCell align='center'>
                                          {<Typography variant='body2'>{row.statDesc}</Typography>}
                                        </TableCell>

                                        <TableCell
                                          align='center'
                                          sx={{ border: `2px solid ${totemColors['blue'].bg}` }}
                                        >
                                          {
                                            <Box
                                              sx={{ alignContent: 'center', display: 'flex', justifyContent: 'center' }}
                                            >
                                              <Typography
                                                variant='body2'
                                                color={totemColors['blue'].bg}
                                                sx={{ marginRight: '2px' }}
                                              >
                                                {blueRow?.stat > 0 ? round(blueRow?.stat, 1) : 0}
                                              </Typography>
                                              {blueRow?.stat === 0
                                                ? ''
                                                : getStatUpDown(blueRow?.stat, purpleRow?.stat, goldRow?.stat) ===
                                                  'blue'
                                                ? tableIcons.up
                                                : tableIcons.down}
                                            </Box>
                                          }
                                        </TableCell>
                                        <TableCell
                                          align='center'
                                          sx={{ border: `2px solid ${totemColors['purple'].bg}` }}
                                        >
                                          {
                                            <Box
                                              sx={{ alignContent: 'center', display: 'flex', justifyContent: 'center' }}
                                            >
                                              <Typography
                                                variant='body2'
                                                color={totemColors['purple'].bg}
                                                sx={{ marginRight: '2px' }}
                                              >
                                                {purpleRow?.stat > 0 ? round(purpleRow.stat, 1) : 0}
                                              </Typography>
                                              {purpleRow?.stat === 0
                                                ? ''
                                                : getStatUpDown(blueRow?.stat, purpleRow?.stat, goldRow?.stat) ===
                                                  'purple'
                                                ? tableIcons.up
                                                : tableIcons.down}
                                            </Box>
                                          }
                                        </TableCell>
                                        <TableCell align='center' sx={{ border: `2px solid #f3990a` }}>
                                          {
                                            <Box
                                              sx={{ alignContent: 'center', display: 'flex', justifyContent: 'center' }}
                                            >
                                              <Typography variant='body2' color='#f3990a' sx={{ marginRight: '2px' }}>
                                                {goldRow?.stat > 0 ? round(goldRow.stat, 1) : 0}
                                              </Typography>
                                              {goldRow?.stat === 0
                                                ? ''
                                                : getStatUpDown(blueRow?.stat, purpleRow?.stat, goldRow?.stat) ===
                                                  'gold'
                                                ? tableIcons.up
                                                : tableIcons.down}
                                            </Box>
                                          }
                                        </TableCell>
                                      </TableRow>
                                    </>
                                  )
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Paper>
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </>
    ),
    calculator: (
      <>
        <Card>
          <Box sx={{ flexGrow: 1 }}>
            <Snackbar
              open={snackOpen.open}
              autoHideDuration={snackOpen.time ?? 6000}
              onClose={snackClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              {snackOpen.open && (
                <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
                  <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
                  {snackOpen.open}
                </Alert>
              )}
            </Snackbar>
            <Grid container spacing={2} sx={{ padding: '0.5em' }}>
              <Grid size={{ xs: 12 }}>
                <Paper elevation={12}>
                  <Typography
                    align='center'
                    variant='h6' component='p'
                    color='primary'
                    sx={{ paddingTop: '0.5em', paddingBottom: '0.5em' }}
                  >
                    RUNESTONES AND TATTOOS CALCULATOR
                  </Typography>
                  <Divider sx={{ marginBottom: '8px' }} />
                  {calculator.map(totem => {
                    const packPriceScroll = totemInfo.scroll[totem.quality]?.avgPrice
                    const packPriceStone = totemInfo.stone[totem.quality]?.avgPrice

                    return (
                      <>
                        <Paper sx={{ border: `2px solid ${totemColors[totem.quality].bg}`, m: '16px' }}>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', marginTop: '8px' }}
                          >
                            {totem.quality === 'blue' && <BlueButton>{totem.quality} Totem</BlueButton>}
                            {totem.quality === 'purple' && <PurpleButton>{totem.quality} Totem</PurpleButton>}
                            {totem.quality === 'gold' && <GoldButton>{totem.quality} Totem</GoldButton>}
                          </Box>
                          <Divider light sx={{ marginBottom: '8px', marginTop: '8px' }} />
                          <Grid container spacing={5}>
                            <Grid size={{ xs: 6, md: 6, lg: 6 }}key={totem.quality + totem.maxLevel + '-stones'}>
                              {/* Stones */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant='body1' color='primary' align='center' sx={{ marginBottom: '8px' }}>
                                  Level (Stones):
                                </Typography>

                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    marginBottom: '8px',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <TextField
                                    align='center'
                                    label='From'
                                    id='fromlevelstones-select'
                                    size='small'
                                    variant='outlined'
                                    type='number'
                                    select
                                    sx={{ marginRight: '5px' }}
                                    value={totem.levelFrom ? totem.levelFrom : ''}
                                    inputProps={{
                                      min: 0,
                                      max: totem.maxLevel
                                    }}
                                    onChange={e => {
                                      const value = +e.target.value
                                      handleCalculatorChange(totem.quality, value, totem.levelTo, 'stones')
                                    }}
                                  >
                                    {[...totemStones[totem.quality]]
                                      ?.filter(t => t.level < totem.maxLevel)
                                      .map(option => (
                                        <MenuItem key={option.level} value={option.level}>
                                          {option.level}
                                        </MenuItem>
                                      ))}
                                  </TextField>

                                  <TextField
                                    disabled={totem.levelFrom > 0 ? false : true}
                                    align='center'
                                    label='To'
                                    id='levelstones-select'
                                    size='small'
                                    variant='outlined'
                                    type='number'
                                    select
                                    sx={{ marginRight: '5px' }}
                                    value={totem.levelTo ? totem.levelTo : ''}
                                    inputProps={{
                                      min: 0,
                                      max: totem.maxLevel
                                    }}
                                    onChange={e => {
                                      const value = +e.target.value
                                      handleCalculatorChange(totem.quality, totem.levelFrom, value, 'stones')
                                    }}
                                  >
                                    {Array.from(Array(totem.maxLevel), (_, i) => i + 1)
                                      .map(n => {
                                        return { level: n }
                                      })
                                      .filter(n => n.level > totem.levelFrom)
                                      .map(option => (
                                        <MenuItem key={option.level} value={option.level}>
                                          {option.level}
                                        </MenuItem>
                                      ))}
                                  </TextField>
                                </Box>

                                <Typography variant='h6' component='h2' color='primary' align='center'>
                                  =
                                </Typography>

                                <Typography variant='h5' component='p' color='textPrimary' align='center'>
                                  {totem.stones?.toLocaleString()}
                                </Typography>

                                <>
                                  <Typography
                                    variant='caption'
                                    color='primary'
                                    align='center'
                                    sx={{ marginLeft: '3px' }}
                                  >
                                    Avg Price ($):
                                  </Typography>

                                  <Typography variant='h5' component='p' align='center' sx={{ marginLeft: '3px' }}>
                                    {`$${round(
                                      (totem.stones / packPriceStone?.qty) * packPriceStone?.p,
                                      0
                                    )?.toLocaleString()} +-`}
                                  </Typography>
                                </>
                              </Box>

                              <Box sx={{ marginBottom: '5px', marginTop: '5px' }}></Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}
                              >
                                <Typography variant='body2' color='primary' align='center' sx={{ marginBottom: '5px' }}>
                                  Your Stones:
                                </Typography>

                                <TextField
                                  slotProps={{ htmlInput: { 'aria-label': 'Your Stones' } }}
                                  id='stones-input'
                                  size='small'
                                  sx={{ maxWidth: '175px', marginLeft: '3px' }}
                                  variant='outlined'
                                  type='number'
                                  placeholder='Quantity'
                                  value={totem.userStones ? totem.userStones : ''}
                                  inputProps={{
                                    min: 0,
                                    max: 999999
                                  }}
                                  onChange={e => {
                                    const level = +e.target.value >= totem.stones ? totem.stones : +e.target.value

                                    handleCalculatorChange(totem.quality, 0, level, 'stonesInput')
                                  }}
                                />

                                {totem.userStones > 0 && (
                                  <>
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginTop: '5px' }}
                                    >
                                      Missing Runestones:
                                    </Typography>
                                    <Typography
                                      variant='h5' component='p'
                                      color='textPrimary'
                                      align='center'
                                      sx={{
                                        color: `${totem.stones - totem.userStones > 0 ? '#C81414' : '#3D7A08'}`
                                      }}
                                    >
                                      {totem.stones - totem.userStones > 0 ? '-' : ''}
                                      {(totem.stones - totem.userStones > 0
                                        ? totem.stones - totem.userStones
                                        : 0
                                      ).toLocaleString()}
                                    </Typography>
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '5px' }}
                                    >
                                      Daily/Weekly quests:
                                    </Typography>
                                    <Typography variant='caption' color='textPrimary' align='center'>
                                      {
                                        getDaysNeeded(
                                          totem.quality === 'blue' ? 'nrs' : 'srs',
                                          totem.stones - totem.userStones > 0 ? totem.stones - totem.userStones : 0
                                        )?.result
                                      }
                                    </Typography>
                                  </>
                                )}
                              </Box>

                              <Divider light sx={{ marginBottom: '8px', marginTop: '8px' }} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 6, lg: 6 }}key={totem.quality + totem.maxLevel + '-tattoos'}>
                              {/* Tattoos */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant='body1' color='primary' align='center' sx={{ marginBottom: '8px' }}>
                                  Skill (Tattoos):
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    marginBottom: '8px',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <TextField
                                    align='center'
                                    label='From'
                                    id='fromlevelskill-select'
                                    size='small'
                                    variant='outlined'
                                    type='number'
                                    select
                                    sx={{ marginRight: '5px' }}
                                    value={totem.skillFrom ? totem.skillFrom : ''}
                                    inputProps={{
                                      min: 0,
                                      max: totem.maxSkill
                                    }}
                                    onChange={e => {
                                      const value = +e.target.value
                                      handleCalculatorChange(totem.quality, value, totem.skillTo, 'skill')
                                    }}
                                  >
                                    {[...totemSkill[totem.quality]]
                                      ?.filter(t => t.level < totem.maxSkill)
                                      .map(option => (
                                        <MenuItem key={option.level} value={option.level}>
                                          {option.level}
                                        </MenuItem>
                                      ))}
                                  </TextField>

                                  <TextField
                                    disabled={totem.skillFrom > 0 ? false : true}
                                    align='center'
                                    label='To'
                                    id='levelskill-select'
                                    size='small'
                                    variant='outlined'
                                    type='number'
                                    select
                                    sx={{ marginRight: '5px' }}
                                    value={totem.skillTo ? totem.skillTo : ''}
                                    inputProps={{
                                      min: 0,
                                      max: totem.maxSkill
                                    }}
                                    onChange={e => {
                                      const value = +e.target.value
                                      handleCalculatorChange(totem.quality, totem.skillFrom, value, 'skill')
                                    }}
                                  >
                                    {Array.from(Array(totem.maxSkill), (_, i) => i + 1)
                                      .map(n => {
                                        return { level: n }
                                      })
                                      .filter(n => n.level > totem.skillFrom)
                                      .map(option => (
                                        <MenuItem key={option.level} value={option.level}>
                                          {option.level}
                                        </MenuItem>
                                      ))}
                                  </TextField>
                                </Box>
                                <Typography variant='h6' component='h2' color='primary' align='center' sx={{ marginLeft: '3px' }}>
                                  =
                                </Typography>

                                <Typography variant='h5' component='p' color='textPrimary' align='center' sx={{ marginLeft: '3px' }}>
                                  {totem.tattoos?.toLocaleString()}
                                </Typography>

                                <>
                                  <Typography
                                    variant='caption'
                                    color='primary'
                                    align='center'
                                    sx={{ marginLeft: '3px' }}
                                  >
                                    Avg Price ($):
                                  </Typography>

                                  <Typography variant='h5' component='p' align='center' sx={{ marginLeft: '3px' }}>
                                    {`$${round(
                                      (totem.tattoos / packPriceScroll?.qty) * packPriceScroll?.p,
                                      0
                                    )?.toLocaleString()} +-`}
                                  </Typography>
                                </>
                              </Box>

                              <Box sx={{ marginBottom: '5px', marginTop: '5px' }}></Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}
                              >
                                <Typography variant='body2' color='primary' align='center' sx={{ marginBottom: '5px' }}>
                                  Your Tattoos:
                                </Typography>

                                <TextField
                                  slotProps={{ htmlInput: { 'aria-label': 'Your Tattoos' } }}
                                  id='tattoos-input'
                                  size='small'
                                  sx={{ maxWidth: '175px', marginLeft: '3px' }}
                                  variant='outlined'
                                  type='number'
                                  placeholder='Quantity'
                                  value={totem.userTattoos ? totem.userTattoos : ''}
                                  inputProps={{
                                    min: 0,
                                    max: 999999
                                  }}
                                  onChange={e => {
                                    const level = +e.target.value >= totem.tattoos ? totem.tattoos : +e.target.value

                                    handleCalculatorChange(totem.quality, 0, level, 'skillInput')
                                  }}
                                />

                                {totem.userTattoos > 0 && (
                                  <>
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginTop: '5px' }}
                                    >
                                      Missing Tattoos:
                                    </Typography>
                                    <Typography
                                      variant='h5' component='p'
                                      color='textPrimary'
                                      align='center'
                                      sx={{
                                        color: `${totem.tattoos - totem.userTattoos > 0 ? '#C81414' : '#3D7A08'}`
                                      }}
                                    >
                                      {totem.tattoos - totem.userTattoos > 0 ? '-' : ''}
                                      {(totem.tattoos - totem.userTattoos > 0
                                        ? totem.tattoos - totem.userTattoos
                                        : 0
                                      ).toLocaleString()}
                                    </Typography>
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '5px' }}
                                    >
                                      Daily/Weekly quests:
                                    </Typography>
                                    <Typography variant='caption' color='textPrimary' align='center'>
                                      {totem.quality === 'gold'
                                        ? '~'
                                        : getDaysNeeded(
                                            totem.quality === 'blue' ? 'bpt' : 'ppt',
                                            totem.tattoos - totem.userTattoos > 0
                                              ? totem.tattoos - totem.userTattoos
                                              : 0
                                          )?.result}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                        <Box sx={{ padding: '0.3rem' }} />
                      </>
                    )
                  })}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </>
    )
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <CustomHeader icon='totem' title='TOTEMS' />
            <Divider sx={{ marginY: '1rem' }} />
            <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}>
              Runestones and Tattoos
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              Totems are powerful buffs in War and Order that enhance specific troop types. Upgrading the right totems for your playstyle can provide significant advantages in both offense and defense.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Configure totem levels to view detailed stat bonuses, then save your configurations to easily compare different totem upgrade paths and find the most efficient investment for your army.
            </Typography>
          </Card>
        </Grid>

        {activeTab === undefined ? (
          ''
        ) : (
          <Grid size={{ xs: 12 }}>
            <TabContext value={activeTab}>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12 }}>
                  <TabList
                    variant='scrollable'
                    scrollButtons='auto'
                    onChange={handleChange}
                    aria-label='customized tabs'
                  >
                    <Tab
                      aria-label='Runestones'
                      value='runestones'
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', ...(!hideText && { '& svg': { mr: 2 } }) }}>
                          <Icon fontSize={20} icon='game-icons:rune-stone' />
                          {'Runestones'}
                        </Box>
                      }
                    />
                    <Tab
                      aria-label='Calculator'
                      value='calculator'
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', ...(!hideText && { '& svg': { mr: 2 } }) }}>
                          <Icon fontSize={20} icon='ion:stats-chart-sharp' />
                          {'Calculator'}
                        </Box>
                      }
                    />
                  </TabList>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ pt: theme => `${theme.spacing(4)} !important` }}>
                  {isLoading ? (
                    <Box sx={{ mt: 6, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <CircularProgress sx={{ mb: 4 }} />
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : (
                    <TabPanel sx={{ p: 0 }} value={activeTab}>
                      {tabContentList[activeTab]}
                    </TabPanel>
                  )}
                </Grid>

                {/*
                  Las dos tablas van fuera del TabPanel a proposito: dentro solo se
                  emitiria la pestaña activa, y la mitad del dato volveria a quedar
                  fuera del HTML, que es justo lo que se esta arreglando.
                */}
                <Grid size={{ xs: 12 }} sx={{ pt: theme => `${theme.spacing(6)} !important` }}>
                  <TablaReferencia
                    titulo='The eight totems: quality, nodes, milestone bonuses and total runestone cost'
                    columnas={COLUMNAS_TOTEMS}
                    filas={FILAS_TOTEMS}
                    idFila='key'
                  />
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ pt: theme => `${theme.spacing(6)} !important` }}>
                  <TablaReferencia
                    titulo='Runestone cost of every upgrade step, by quality, node and level'
                    columnas={COLUMNAS_PIEDRAS}
                    filas={FILAS_PIEDRAS}
                    idFila='id'
                  />
                </Grid>
              </Grid>
            </TabContext>
          </Grid>
        )}
      </Grid>
    </>
  )
}

Totems.noLoadingGate = true

export default Totems
